import { css } from 'aphrodite';
import moment from 'moment';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { ICustomSurvey } from '../../../../../extViewmodels';
import { CustomDateRange } from '../../../../components/CustomDateRange';
import { Popover, PopoverContent, PopoverTrigger } from '../../../../components-v2/Popover';
import { RadioButton } from '../../../../components/RadioButton';
import { CalendarDateIcon } from '../../../../components/svgs/icons/CalendarDateIcon';
import { brandPrimaryHover } from '../../../../styles/colors';
import { usePreviewMode } from '../../shared/contexts/PreviewModeContext';
import { styleSheet } from '../styles';

export const DateRangeSettings = ({ survey }: { survey: ICustomSurvey }) => {
	const { isPreviewOnly } = usePreviewMode();
	const isArchived = !!survey?.archivedDate;
	const isDisabled = isArchived || isPreviewOnly;
	const [dateRangeModalIsOpen, setDateRangeModalIsOpen] = React.useState<boolean>(false);

	const { control, setValue, watch } = useFormContext<ICustomSurvey>();
	const formValues = watch();
	const hasDateRange = !!formValues.startDate && !!formValues?.expirationDate;

	const handleCloseDateRangeModal = () => {
		setDateRangeModalIsOpen(false);
	};

	const onEditDateRangeClicked = React.useCallback((e: React.MouseEvent<HTMLElement>) => {
		setDateRangeModalIsOpen(true);
		e.stopPropagation();
		e.preventDefault();
	}, []);

	return (
		<section className={css(styleSheet.tabsControllerWrap)}>
			<h3>Survey Date Range</h3>
			<div className={css(styleSheet.formControl)}>
				<Controller
					name='hasDateRange'
					control={control}
					render={({ field: { ref, ...fieldProps } }) => (
						<div className={css(styleSheet.radioButtons)}>
							<RadioButton
								checked={!hasDateRange}
								disabled={isDisabled}
								id={'specific-date-range-1'}
								onChange={() => {
									fieldProps.onChange(false);
									setValue('expirationDate', undefined, { shouldDirty: true });
									setValue('startDate', new Date().toISOString(), { shouldDirty: true });
								}}
							>
								<span>Survey is available indefinitely into the future</span>
							</RadioButton>

							<Popover open={dateRangeModalIsOpen} onOpenChange={handleCloseDateRangeModal}>
								<PopoverTrigger>
									<RadioButton
										checked={hasDateRange}
										disabled={isDisabled}
										id={'specific-date-range'}
										onChange={() => {
											fieldProps.onChange(true);
											setDateRangeModalIsOpen(true);
										}}
									>
										<span>Survey is only available for a specific date range &nbsp;</span>
									</RadioButton>
								</PopoverTrigger>
								<PopoverContent side='right'>
									<CustomDateRange
										minDate={new Date()}
										from={formValues.startDate ? new Date(formValues.startDate) : undefined}
										to={formValues.expirationDate ? new Date(formValues.expirationDate) : undefined}
										resetDates={() => {
											setValue('expirationDate', undefined, { shouldDirty: true });
											setValue('startDate', new Date().toISOString(), { shouldDirty: true });
										}}
										endDateTextLabel='End Date'
										endDateRequired={true}
										onChange={(from, to) => {
											setValue('startDate', moment(from).toISOString(), { shouldDirty: true });
											setValue(
												'expirationDate',
												moment(to).isValid() ? moment(to).endOf('day').toISOString() : undefined,
												{ shouldDirty: true }
											);
										}}
									/>
								</PopoverContent>
							</Popover>

							{hasDateRange && (
								<button
									className={css(styleSheet.calendarDateRangeButton)}
									disabled={isDisabled}
									onClick={onEditDateRangeClicked}
								>
									<CalendarDateIcon fillColor={brandPrimaryHover} />{' '}
									<span>
										{`${moment(formValues.startDate).format('MM/DD/yyyy')} - ${moment(formValues.expirationDate).format(
											'MM/DD/yyyy'
										)}`}
									</span>
								</button>
							)}
						</div>
					)}
				/>
			</div>
		</section>
	);
};
